/** Display/progress coverage only; this does not authorize or issue trading commands. */
import type { SessionStatus } from "../gen/helm/v1/common_pb";
import { SESSION_STATUS } from "./gate.ts";
import { unconfirmedAccountIds, type TradingState } from "./state.ts";

const READY: SessionStatus = SESSION_STATUS.READY;

/** SessionState covers the primary login only. Empty is the explicit env-primary identity. */
export function confirmedBookAccountIds(
  s: TradingState,
  connected: boolean,
  replayReady: boolean,
): ReadonlySet<string> {
  const confirmed = new Set<string>();
  const session = s.session;
  if (
    !session ||
    !connected ||
    !replayReady ||
    session.status !== READY ||
    typeof session.loginId !== "string"
  )
    return confirmed;
  const uncertain = new Set(unconfirmedAccountIds(s));
  for (const [id, account] of s.accounts) {
    if (
      account.accountId === id &&
      typeof account.loginId === "string" &&
      account.loginId === session.loginId &&
      !uncertain.has(id)
    )
      confirmed.add(id);
  }
  return confirmed;
}

export type BookIdentity = { sessionId: string | null; bootId: string | null; loginId: string | null };
export const bookIdentity = (s: TradingState): BookIdentity => ({
  sessionId: s.sessionId,
  bootId: s.bootId,
  loginId: s.session?.loginId ?? null,
});

/** An older exit watch must not be completed by a replacement login/process/session's empty book. */
export function exitScopeConfirmed(
  s: TradingState,
  accounts: readonly string[],
  identity: BookIdentity,
  connected: boolean,
  replayReady: boolean,
): boolean {
  const current = bookIdentity(s);
  if (
    current.sessionId !== identity.sessionId ||
    current.bootId !== identity.bootId ||
    current.loginId !== identity.loginId
  )
    return false;
  const confirmed = confirmedBookAccountIds(s, connected, replayReady);
  return accounts.length > 0 && accounts.every((id) => confirmed.has(id));
}
